import { useMemo } from "react";
import { comics } from "@/mocks/comics";
import ComicCard from "./ComicCard";

interface FeaturedProps {
  search: string;
  onClearSearch: () => void;
  tab: string;
  onTabChange: (tab: string) => void;
}

const tabs = ["全部", "少女漫画", "甜宠漫画", "萌系漫画", "治愈漫画"];

export default function Featured({ search, onClearSearch, tab, onTabChange }: FeaturedProps) {
  const list = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return comics.filter((c) => {
      const matchTab = tab === "全部" || c.categories.includes(tab);
      if (!matchTab) return false;
      if (!keyword) return true;
      return (
        c.title.toLowerCase().includes(keyword) ||
        c.author.toLowerCase().includes(keyword) ||
        c.categories.some((cat) => cat.toLowerCase().includes(keyword)) ||
        c.description.includes(keyword)
      );
    });
  }, [search, tab]);

  return (
    <section id="featured" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold text-primary-600">编辑部精选</p>
          <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">精选作品</h2>
        </div>

        <div className="inline-flex w-fit max-w-full gap-1 rounded-full bg-background-100 p-1">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onTabChange(t)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                tab === t
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:bg-primary-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <p className="text-sm text-foreground-600">
          共 <span className="font-semibold text-primary-600">{list.length}</span> 部作品
          {search.trim() && (
            <>
              ，为你找到与「<span className="font-semibold text-foreground-950">{search}</span>」相关的漫画
            </>
          )}
        </p>
        {search.trim() && (
          <button
            type="button"
            onClick={onClearSearch}
            className="whitespace-nowrap rounded-md bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            <i className="ri-close-line mr-1" aria-hidden="true"></i>
            清除搜索
          </button>
        )}
      </div>

      {list.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {list.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-lg bg-background-100 py-16 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true"></i>
          <p className="mt-3 text-sm text-foreground-600">暂时没有找到「{search}」相关的漫画，换个关键词试试吧。</p>
          <button
            type="button"
            onClick={onClearSearch}
            className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            查看全部作品
          </button>
        </div>
      )}
    </section>
  );
}